import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// ─── Public API ───────────────────────────────────────────────────────────────

export interface PathologyPermissions {
    /** Upload WSI slide studies (.dcm / .zip) */
    canUploadStudy: boolean;
    /** Upload / replace pathology report files */
    canUploadReport: boolean;
    /** Delete pathology report files */
    canDeleteReport: boolean;
    /** View (open / preview) pathology reports */
    canViewReports: boolean;
    /** True while the Cognito session is being fetched */
    loading: boolean;
    /** Identity of the signed-in user, for stamping local case-workflow entries. Null while loading or if identity can't be resolved. */
    currentUser: { id: string; name: string } | null;
}

// ─── Cognito group → role mapping ─────────────────────────────────────────────

// Match any of these group names (case-insensitive) as each clinical role.
const ADMIN_GROUPS  = ['admin', 'admins', 'administrator', 'administrators'];
const DOCTOR_GROUPS = ['doctor', 'doctors', 'physician', 'physicians', 'provider', 'providers'];
const NURSE_GROUPS  = ['nurse', 'nurses', 'staff', 'nursing'];

const matchGroups = (userGroups: string[], targets: string[]): boolean =>
    userGroups.some(g => targets.includes(g.toLowerCase()));

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Reads the authenticated user's Cognito group membership and returns
 * per-action capability flags for the Pathology module.
 *
 * Role matrix:
 *  Admin / Doctor : canUploadStudy ✓  canUploadReport ✓  canDeleteReport ✓  canViewReports ✓
 *  Nurse          : canUploadStudy ✓  canUploadReport ✗  canDeleteReport ✗  canViewReports ✓
 *  (no group)     : Full access — mirrors the previous behaviour where no role
 *                   checks existed, ensuring zero regression for existing users.
 */
export function usePathologyPermissions(): PathologyPermissions {
    const [perms, setPerms] = useState<PathologyPermissions>({
        canUploadStudy:  true,
        canUploadReport: true,
        canDeleteReport: true,
        canViewReports:  true,
        loading:         true,
        currentUser:     null,
    });

    useEffect(() => {
        let cancelled = false;

        fetchAuthSession()
            .then(session => {
                if (cancelled) return;

                const payload    = session.tokens?.idToken?.payload ?? ({} as any);
                const rawGroups  = (payload['cognito:groups'] as string[] | undefined) ?? [];
                const currentUser = {
                    id: String(payload?.sub ?? payload?.email ?? payload?.['cognito:username'] ?? ''),
                    name: String(payload?.name ?? payload?.email ?? payload?.['cognito:username'] ?? 'Unknown user'),
                };

                const isAdmin  = matchGroups(rawGroups, ADMIN_GROUPS);
                const isDoctor = matchGroups(rawGroups, DOCTOR_GROUPS);
                const isNurse  = matchGroups(rawGroups, NURSE_GROUPS);
                const hasGroup = isAdmin || isDoctor || isNurse;

                setPerms({
                    canUploadStudy:  true,                              // all authenticated users
                    canUploadReport: !hasGroup || isAdmin || isDoctor,  // nurses excluded
                    canDeleteReport: !hasGroup || isAdmin || isDoctor,  // nurses excluded
                    canViewReports:  true,                              // all authenticated users
                    loading:         false,
                    currentUser,
                });
            })
            .catch(() => {
                if (!cancelled) {
                    // Fail open — grant full access on auth error to avoid blocking clinical workflows.
                    setPerms({
                        canUploadStudy:  true,
                        canUploadReport: true,
                        canDeleteReport: true,
                        canViewReports:  true,
                        loading:         false,
                        currentUser:     null,
                    });
                }
            });

        return () => { cancelled = true; };
    }, []);

    return perms;
}
